import { type FC } from 'react';

import { Box } from '../Box';
import { Divider } from '../Divider';
import { Icon } from '../Icon';
import { Link } from '../Link';
import { Logo } from '../Logo';
import { Text } from '../Typography';
import {
  footerBottomContentStyles,
  footerBottomSectionStyles,
  footerBrandStyles,
  footerLinkColumnStyles,
  footerLinkGroupStyles,
  footerLinksRowStyles,
  footerLinkStyles,
  footerLogoStyles,
  footerSectionStyles,
  footerSocialIconStyles,
  footerSocialLinksStyles,
  footerSocialLinkStyles,
  footerStyles,
  footerTopSectionStyles,
} from './Footer.styles';
import {
  type FooterLink,
  type FooterLinkGroup,
  type FooterProps,
} from './Footer.types';

const normalizeLinkGroups = (
  groups?: FooterLinkGroup[],
  flat?: FooterLink[]
): FooterLinkGroup[] => {
  if (groups && groups.length > 0) {
    return groups;
  }

  if (flat && flat.length > 0) {
    return [flat];
  }

  return [];
};

const Footer: FC<FooterProps> = ({
  tagline,
  primaryLinkGroups,
  secondaryLinkGroups,
  primaryLinks = [],
  secondaryLinks = [],
  highlightLink,
  socialLinks = [],
  logoVariant = 'standard',
  logoHref = '/',
  linkAs = 'a',
  className,
}) => {
  const primaryGroups = normalizeLinkGroups(primaryLinkGroups, primaryLinks);
  const secondaryGroups = normalizeLinkGroups(
    secondaryLinkGroups,
    secondaryLinks
  );

  const renderLinkGroup = (group: FooterLinkGroup, groupIndex: number) => (
    <Box as="ul" css={footerLinkGroupStyles} key={groupIndex}>
      {group.map((link) => (
        <Box as="li" key={link.href}>
          <Link
            as={linkAs}
            href={link.href}
            onClick={link.onClick}
            underline="none"
            css={footerLinkStyles}
          >
            <Text as="span" size="md" fontWeight="semibold">
              {link.label}
            </Text>
          </Link>
        </Box>
      ))}
    </Box>
  );

  const renderLinkColumn = (groups: FooterLinkGroup[]) => {
    if (groups.length === 0) return null;

    return (
      <Box css={footerLinkColumnStyles}>
        {groups.map((group, index) => renderLinkGroup(group, index))}
      </Box>
    );
  };

  const hasLinkColumns = primaryGroups.length > 0 || secondaryGroups.length > 0;
  const hasBottomSection = Boolean(highlightLink) || socialLinks.length > 0;

  return (
    <Box as="footer" css={footerStyles} className={className}>
      <Box css={footerSectionStyles}>
        <Box css={footerTopSectionStyles}>
          <Box css={footerBrandStyles}>
            <Link as={linkAs} href={logoHref} underline="none">
              <Box css={footerLogoStyles}>
                <Logo variant={logoVariant} theme="light" />
              </Box>
            </Link>

            {tagline && (
              <Text size="md" fontWeight="semibold">
                {tagline}
              </Text>
            )}
          </Box>

          {hasLinkColumns && (
            <Box css={footerLinksRowStyles}>
              {renderLinkColumn(primaryGroups)}
              {renderLinkColumn(secondaryGroups)}
            </Box>
          )}
        </Box>
      </Box>

      {hasBottomSection && (
        <Box css={footerSectionStyles}>
          <Box css={footerBottomSectionStyles}>
            <Divider />

            <Box css={footerBottomContentStyles}>
              {highlightLink && (
                <Link
                  as={linkAs}
                  href={highlightLink.href}
                  onClick={highlightLink.onClick}
                  underline="always"
                >
                  <Text as="span" size="md">
                    {highlightLink.label}
                  </Text>
                </Link>
              )}

              {socialLinks.length > 0 && (
                <Box as="ul" css={footerSocialLinksStyles}>
                  {socialLinks.map((social) => (
                    <Box as="li" key={social.href}>
                      <Link
                        as={linkAs}
                        href={social.href}
                        aria-label={social.label}
                        underline="none"
                        css={footerSocialLinkStyles}
                      >
                        <Icon
                          variant={social.icon}
                          css={footerSocialIconStyles}
                        />
                      </Link>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Footer;
